import type { HTMLAttributes, ReactNode } from "react";
import styles from "./Headings.module.css";

type HeadingColor = "default" | "text" | "primary" | "muted" | "onPrimary" | "inherit";
type HeadingAlign = "left" | "center";
type H1Variant = "default" | "compactBottom" | "responderTitle" | "journeyCard";
type H2Variant = "default" | "compact" | "journeyCard";
type H3Variant = "default" | "noTop" | "compact" | "flush" | "dense" | "section" | "spaced" | "noTopDense" | "journeyCard" | "messageItem";

type HeadingProps<TVariant extends string> = Omit<HTMLAttributes<HTMLHeadingElement>, "children" | "color"> & {
  children: ReactNode;
  variant?: TVariant;
  color?: HeadingColor;
  align?: HeadingAlign;
};

const getHeadingClassName = <TLevel extends "h1" | "h2" | "h3" | "h4" | "h5" | "h6">(
  level: TLevel,
  variant: string,
  color: HeadingColor,
  align: HeadingAlign,
  className?: string,
) =>
  [
    styles.heading,
    styles[level],
    variant !== "default" && styles[`${level}_${variant}`],
    color !== "default" && styles[`color_${color}`],
    align === "center" && styles.alignCenter,
    className,
  ]
    .filter(Boolean)
    .join(" ");

export const H1 = ({ children, variant = "default", color = "default", align = "left", className, ...props }: HeadingProps<H1Variant>) => (
  <h1 className={getHeadingClassName("h1", variant, color, align, className)} {...props}>
    {children}
  </h1>
);

export const H2 = ({ children, variant = "default", color = "default", align = "left", className, ...props }: HeadingProps<H2Variant>) => (
  <h2 className={getHeadingClassName("h2", variant, color, align, className)} {...props}>
    {children}
  </h2>
);

export const H3 = ({ children, variant = "default", color = "default", align = "left", className, ...props }: HeadingProps<H3Variant>) => (
  <h3 className={getHeadingClassName("h3", variant, color, align, className)} {...props}>
    {children}
  </h3>
);
